"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Activity,
  ArrowRight,
  BrainCircuit,
  CircleDot,
  CloudCog,
  Database,
  Fingerprint,
  Radio,
  RefreshCw,
  Scale,
  ShieldAlert,
  Signal,
  WifiOff,
  Zap,
  type LucideIcon,
} from "lucide-react";
import { subscribeNexusFabricEvents } from "../lib/nexusEventBus";
import { formatFabricAge, mergeFabricEvents, normalizeFabricEvent } from "./livingData";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "./ui";
import "./nexus-living.css";

type FabricStatus = "connecting" | "live" | "reconnecting" | "degraded" | "offline";

type FabricEvent = ReturnType<typeof normalizeFabricEvent>;

const FABRIC_NODES: Array<{ icon: LucideIcon; label: string; sub: string; view: string }> = [
  { icon: Database, label: "DATA", sub: "MARKET", view: "intelligence" },
  { icon: CloudCog, label: "PIPELINE", sub: "OPS", view: "operations" },
  { icon: BrainCircuit, label: "AGENTS", sub: "MESH", view: "agents" },
  { icon: Scale, label: "CAPITAL", sub: "GATE", view: "capital" },
  { icon: Fingerprint, label: "PROOF", sub: "LEDGER", view: "proof" },
];

function parseSsePayload(event: MessageEvent) {
  try {
    return JSON.parse(event.data || "{}");
  } catch {
    return {};
  }
}

function statusLabel(status: FabricStatus) {
  if (status === "live") return "LIVE";
  if (status === "connecting") return "CONNECT";
  if (status === "reconnecting") return "REPLAY";
  if (status === "offline") return "OFFLINE";
  return "DEGRADED";
}

function EventGlyph({ event }: { event: FabricEvent }) {
  if (event.type === "pr" || event.type === "approval" || event.priority === "critical") return <ShieldAlert />;
  if (event.type === "agent") return <BrainCircuit />;
  if (event.type === "market") return <Activity />;
  if (event.type === "slots") return <Zap />;
  return <CircleDot />;
}

export default function NexusLiveFabric({
  bridgeState,
  activeView,
  onNavigate,
}: {
  bridgeState: "connected" | "degraded" | "unknown";
  activeView: string;
  onNavigate: (view: string) => void;
}) {
  const [status, setStatus] = useState<FabricStatus>(() => typeof navigator !== "undefined" && navigator.onLine === false ? "offline" : "connecting");
  const [events, setEvents] = useState<FabricEvent[]>([]);
  const [lastHeartbeatAt, setLastHeartbeatAt] = useState<string | null>(null);
  const [sessionCount, setSessionCount] = useState(0);
  const [signalCount, setSignalCount] = useState(0);
  const [now, setNow] = useState(Date.now());
  const sourceRef = useRef<EventSource | null>(null);
  const activeViewRef = useRef(activeView);

  const addEvent = useCallback((input: unknown) => {
    const normalized = normalizeFabricEvent(input as Record<string, unknown>);
    setEvents((current) => mergeFabricEvents(current, [normalized], 24));
    setSignalCount((current) => current + 1);
  }, []);

  useEffect(() => subscribeNexusFabricEvents(addEvent), [addEvent]);

  useEffect(() => { activeViewRef.current = activeView; }, [activeView]);

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1_000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    let disposed = false;

    const close = () => {
      sourceRef.current?.close();
      sourceRef.current = null;
    };

    const connect = () => {
      close();
      if (disposed) return;
      if (navigator.onLine === false) {
        setStatus("offline");
        return;
      }
      if (typeof EventSource === "undefined") {
        setStatus("degraded");
        return;
      }

      setStatus((current) => current === "live" ? "reconnecting" : "connecting");
      const source = new EventSource("/api/hqs-core?action=stream&topics=market,slots&slotsLimit=3");
      sourceRef.current = source;

      source.addEventListener("hello", (event) => {
        if (disposed) return;
        const payload = parseSsePayload(event as MessageEvent);
        const createdAt = payload.startedAt || new Date().toISOString();
        setStatus("live");
        setLastHeartbeatAt(createdAt);
        setSessionCount((current) => current + 1);
        addEvent({
          type: "system",
          source: "vercel_sse",
          sourceId: `session:${createdAt}`,
          title: "Live Fabric verbunden",
          detail: `${Array.isArray(payload.topics) ? payload.topics.join(" · ") : "Market · Slots"} · EventSource`,
          priority: "info",
          createdAt,
          targetView: activeViewRef.current,
        });
      });

      source.addEventListener("pulse", (event) => {
        if (disposed) return;
        const payload = parseSsePayload(event as MessageEvent);
        setStatus("live");
        setLastHeartbeatAt(payload.t || new Date().toISOString());
        setSignalCount((current) => current + 1);
      });

      source.addEventListener("market", (event) => {
        const payload = parseSsePayload(event as MessageEvent);
        const count = Array.isArray(payload.stocks) ? payload.stocks.length : 0;
        addEvent({
          type: "market",
          source: "market_stream",
          sourceId: `market:${payload.updatedAt || payload.generatedAt || count || payload.notice || "empty"}`,
          title: payload.success ? "Marktzustand aktualisiert" : "Market Cache wartet",
          detail: payload.success ? `${count} Assets · ${payload.source || "Live Cache"}` : "SSE aktiv · noch kein Redis-Snapshot",
          priority: payload.success ? "info" : "medium",
          createdAt: payload.updatedAt || payload.generatedAt || new Date().toISOString(),
          targetView: "intelligence",
        });
      });

      source.addEventListener("slots", (event) => {
        const payload = parseSsePayload(event as MessageEvent);
        const count = Array.isArray(payload.slots) ? payload.slots.length : 0;
        addEvent({
          type: "slots",
          source: "slot_stream",
          sourceId: `slots:${payload.updatedAt || count || payload.notice || "empty"}`,
          title: payload.success ? "Chancen-Slots aktualisiert" : "Slot Cache wartet",
          detail: payload.success ? `${count} priorisierte Chancen · ${payload.source || "Live Cache"}` : "Stream aktiv · keine Cache-Daten",
          priority: "info",
          createdAt: payload.updatedAt || new Date().toISOString(),
          targetView: "intelligence",
        });
      });

      source.addEventListener("bye", () => {
        if (!disposed) setStatus("reconnecting");
      });

      source.onerror = () => {
        if (!disposed) setStatus(navigator.onLine === false ? "offline" : "reconnecting");
      };
    };

    const handleVisibility = () => {
      if (document.visibilityState === "hidden") {
        close();
        setStatus("reconnecting");
      } else {
        connect();
      }
    };
    const handleOnline = () => connect();
    const handleOffline = () => { close(); setStatus("offline"); };

    connect();
    document.addEventListener("visibilitychange", handleVisibility);
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    return () => {
      disposed = true;
      close();
      document.removeEventListener("visibilitychange", handleVisibility);
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, [addEvent]);

  const heartbeatAge = lastHeartbeatAt ? formatFabricAge(lastHeartbeatAt, now) : "—";
  const meaningfulEvents = useMemo(() => events.filter((event) => event.type !== "system"), [events]);
  const latest = meaningfulEvents[0] || events[0];
  const effectiveStatus: FabricStatus = status === "live" && bridgeState !== "connected" ? (bridgeState === "unknown" ? "connecting" : "degraded") : status;

  return (
    <div className="context-ribbon__stream nexus-fabric-slot">
      <Sheet>
        <SheetTrigger asChild>
          <button type="button" className={`nexus-fabric-trigger nexus-fabric-trigger--${effectiveStatus}`} aria-label="HQS Live Event Fabric öffnen">
            <span className="nexus-fabric-trigger__radar" aria-hidden="true"><i /><i /><b /></span>
            <span className="nexus-fabric-trigger__copy">
              <small><Radio /> HQS SIGNAL FABRIC</small>
              <strong>{statusLabel(effectiveStatus)}</strong>
            </span>
            <i className="nexus-fabric-trigger__divider" />
            <span className="nexus-fabric-trigger__event">{latest ? latest.title : "Ereignisse werden verbunden"}</span>
            <span className="nexus-fabric-trigger__age">{heartbeatAge}</span>
          </button>
        </SheetTrigger>
        <SheetContent className="nexus-fabric-sheet">
          <div className="nexus-fabric-sheet__aurora" aria-hidden="true"><i /><i /><i /></div>
          <SheetHeader className="nexus-fabric-sheet__header">
            <span><Signal /> LIVING INTELLIGENCE LAYER</span>
            <SheetTitle>HQS Event Fabric</SheetTitle>
            <SheetDescription>Ein gemeinsamer Live-Strom für Markt, Chancen, Agenten, Freigaben und Systemzustände.</SheetDescription>
          </SheetHeader>

          <section className={`fabric-core fabric-core--${effectiveStatus}`}>
            <div className="fabric-core__visual" aria-hidden="true"><span><Radio /></span><i /><i /><i /></div>
            <div><small>CURRENT FABRIC STATE</small><strong>{statusLabel(effectiveStatus)}</strong><p>{latest ? `${latest.title} · ${latest.detail}` : "SSE-Verbindung und lokale Ereigniskanäle werden initialisiert."}</p></div>
          </section>

          <section className="fabric-metrics" aria-label="Event-Fabric Kennzahlen">
            <article><span>HEARTBEAT</span><strong>{heartbeatAge}</strong><small>seit letztem Puls</small></article>
            <article><span>SIGNALS</span><strong>{signalCount}</strong><small>diese Sitzung</small></article>
            <article><span>SESSIONS</span><strong>{sessionCount}</strong><small>reconnect-safe</small></article>
            <article><span>EVENTS</span><strong>{events.length}</strong><small>dedupliziert</small></article>
          </section>

          <section className="fabric-topology" aria-label="HQS Event-Topologie">
            {FABRIC_NODES.map(({ icon: Icon, label, sub, view }, index) => (
              <div key={label}>
                <button type="button" onClick={() => onNavigate(view)}><Icon /><strong>{label}</strong><small>{sub}</small></button>
                {index < 4 ? <span><i /><ArrowRight /></span> : null}
              </div>
            ))}
          </section>

          <section className="fabric-event-log">
            <header><div><span><Activity /> LIVE EVENT LEDGER</span><strong>{events.length > 0 ? `${events.length} letzte Zustandswechsel` : "Noch keine Ereignisse"}</strong></div><RefreshCw className={effectiveStatus === "connecting" || effectiveStatus === "reconnecting" ? "spin" : ""} /></header>
            <div className="fabric-event-log__stream" aria-live="polite">
              {events.length === 0 ? (
                <div className="fabric-event-empty"><WifiOff /><strong>Der Ereignisstrom wird aufgebaut.</strong><span>Lokale Meldungen bleiben auch ohne SSE sichtbar.</span></div>
              ) : events.map((event) => (
                <button type="button" key={event.id} className={`fabric-event fabric-event--${event.priority}`} onClick={() => event.targetView && onNavigate(event.targetView)} disabled={!event.targetView}>
                  <span className="fabric-event__icon"><EventGlyph event={event} /></span>
                  <span><small>{event.source.replaceAll("_", " ")} · {formatFabricAge(event.createdAt, now)}</small><strong>{event.title}</strong><p>{event.detail}</p></span>
                  {event.targetView ? <ArrowRight /> : <CircleDot />}
                </button>
              ))}
            </div>
          </section>
        </SheetContent>
      </Sheet>
    </div>
  );
}
